import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createChart, ColorType, LineStyle, CrosshairMode } from 'lightweight-charts';
import { analyzeSymbol, getAnalysisHistory, getRecentAnalyses } from '../api/elliottWaveApi';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ─── Wave Color Palette ────────────────────────────────────────────
const WAVE_COLORS = {
  impulse: {
    '1': '#2563eb', // blue-600
    '2': '#7c3aed', // violet-600
    '3': '#059669', // emerald-600
    '4': '#d97706', // amber-600
    '5': '#dc2626', // red-600
  },
  correction: {
    A: '#ef4444', // red-500
    B: '#f59e0b', // amber-500
    C: '#8b5cf6', // violet-500
  },
};

const FIBONACCI_COLORS = {
  '23.6%': '#94a3b8',
  '38.2%': '#60a5fa',
  '50%': '#a78bfa',
  '61.8%': '#f59e0b',
  '78.6%': '#f97316',
  '100%': '#ef4444',
  '127.2%': '#ec4899',
  '161.8%': '#14b8a6',
  '200%': '#22d3ee',
  '261.8%': '#a855f7',
};

const CHANNEL_COLORS = {
  upper: '#22c55e',
  lower: '#ef4444',
  mid: '#94a3b8',
};

const TIMEFRAMES = [
  { value: '5min', label: '5 Min' },
  { value: '15min', label: '15 Min' },
  { value: '1hour', label: '1 Hour' },
  { value: '1day', label: 'Daily' },
];

// ─── Confidence Gauge Component ─────────────────────────────────────
const ConfidenceGauge = ({ value, label, size = 'md' }) => {
  const percentage = Math.round((value || 0) * 100);
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const color =
    percentage >= 70 ? '#22c55e' : percentage >= 40 ? '#f59e0b' : '#ef4444';
  const dimensions = size === 'lg' ? 'w-28 h-28' : size === 'sm' ? 'w-16 h-16' : 'w-20 h-20';
  const textSize = size === 'lg' ? 'text-xl' : size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <div className="flex flex-col items-center">
      <div className={`relative ${dimensions}`}>
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="40" fill="none" stroke="#1e293b" strokeWidth="8" />
          <circle
            cx="50" cy="50" r="40" fill="none"
            stroke={color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`${textSize} font-bold`} style={{ color }}>{percentage}%</span>
        </div>
      </div>
      {label && <span className="text-xs text-gray-400 mt-1 text-center">{label}</span>}
    </div>
  );
};

// ─── Direction Badge Component ──────────────────────────────────────
const DirectionBadge = ({ direction, className = '' }) => {
  const config = {
    up: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', icon: '▲', label: 'BULLISH' },
    down: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', icon: '▼', label: 'BEARISH' },
    sideways: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30', icon: '◆', label: 'SIDEWAYS' },
  };
  const c = config[direction] || config.sideways;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border ${c.bg} ${c.text} ${c.border} ${className}`}>
      <span>{c.icon}</span> {c.label}
    </span>
  );
};

// ─── Info Card Component ────────────────────────────────────────────
const InfoCard = ({ title, children, className = '', headerRight = null }) => (
  <div className={`bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-xl overflow-hidden ${className}`}>
    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50 bg-slate-800/80">
      <h3 className="text-sm font-semibold text-gray-200 uppercase tracking-wider">{title}</h3>
      {headerRight}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════
// ─── MAIN PAGE COMPONENT ─────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
const ElliottWavePage = () => {
  // ── State ──────────────────────────────────────────────────────
  const [symbol, setSymbol] = useState('NIFTY50');
  const [timeframe, setTimeframe] = useState('1day');
  const [includeMtf, setIncludeMtf] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [activeTab, setActiveTab] = useState('chart'); // chart | history
  const [showFibLevels, setShowFibLevels] = useState(true);
  const [showChannels, setShowChannels] = useState(true);
  const [showWaveLabels, setShowWaveLabels] = useState(true);
  const [mtfTimeframe, setMtfTimeframe] = useState(null);

  // ── Chart refs ─────────────────────────────────────────────────
  const chartContainerRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const candlestickSeriesRef = useRef(null);
  const fibLinesRef = useRef([]);
  const channelLinesRef = useRef([]);
  const markersRef = useRef([]);

  // ── Analyze handler ────────────────────────────────────────────
  const handleAnalyze = useCallback(async () => {
    if (!symbol.trim()) {
      setError('Please enter a symbol');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await analyzeSymbol(symbol.trim(), timeframe, includeMtf);
      setAnalysis(result);
      setActiveTab('chart');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Analysis failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      console.error('Elliott Wave analysis error:', err);
    } finally {
      setLoading(false);
    }
  }, [symbol, timeframe, includeMtf]);

  // ── Load history ───────────────────────────────────────────────
  const loadHistory = useCallback(async () => {
    try {
      const [hist, recent] = await Promise.all([
        getAnalysisHistory(symbol.trim(), null, 20).catch(() => []),
        getRecentAnalyses(20).catch(() => []),
      ]);
      setHistory(hist);
      setRecentAnalyses(recent);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  }, [symbol]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, loadHistory]);

  // ═══════════════════════════════════════════════════════════════
  // ─── CHART RENDERING ──────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!analysis || !chartContainerRef.current) return;

    // Destroy previous chart
    if (chartInstanceRef.current) {
      chartInstanceRef.current.remove();
      chartInstanceRef.current = null;
    }

    const container = chartContainerRef.current;

    // Create chart
    const chart = createChart(container, {
      width: container.clientWidth,
      height: 520,
      layout: {
        background: { type: ColorType.Solid, color: '#0f172a' },
        textColor: '#94a3b8',
        fontFamily: "'Inter', sans-serif",
      },
      grid: {
        vertLines: { color: '#1e293b' },
        horzLines: { color: '#1e293b' },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: '#475569', labelBackgroundColor: '#334155' },
        horzLine: { color: '#475569', labelBackgroundColor: '#334155' },
      },
      rightPriceScale: {
        borderColor: '#334155',
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      timeScale: {
        borderColor: '#334155',
        timeVisible: true,
        secondsVisible: false,
      },
    });

    chartInstanceRef.current = chart;

    // ── Build OHLC data from wave points ──────────────────────
    // The analysis contains wave_structure.waves which are pivot points.
    // We'll create a synthetic price line if we don't have full candle data,
    // or use the wave points to mark on any available series.

    const waves = analysis.wave_structure?.waves || [];
    const patternType = analysis.wave_structure?.pattern_type || 'impulse';

    // Create line series from wave points as the primary visualization
    // since the API returns pivot-based analysis, not full OHLC data
    if (waves.length > 0) {
      // Build line data from wave points
      const lineData = waves
        .filter((w) => w.price != null && (w.timestamp || w.index != null))
        .map((w, idx) => ({
          time: w.timestamp
            ? Math.floor(new Date(w.timestamp).getTime() / 1000)
            : Math.floor(Date.now() / 1000) - (waves.length - idx) * (timeframe === '1day' ? 86400 : timeframe === '1hour' ? 3600 : timeframe === '15min' ? 900 : 300),
          value: w.price,
        }))
        .sort((a, b) => a.time - b.time);

      // Ensure unique times
      const uniqueLineData = [];
      const seenTimes = new Set();
      for (const d of lineData) {
        if (!seenTimes.has(d.time)) {
          seenTimes.add(d.time);
          uniqueLineData.push(d);
        }
      }

      if (uniqueLineData.length >= 2) {
        // Add area series for wave structure visualization
        const areaSeries = chart.addAreaSeries({
          lineColor: patternType === 'impulse' ? '#3b82f6' : '#a78bfa',
          topColor: patternType === 'impulse' ? 'rgba(59,130,246,0.25)' : 'rgba(167,139,250,0.25)',
          bottomColor: patternType === 'impulse' ? 'rgba(59,130,246,0.02)' : 'rgba(167,139,250,0.02)',
          lineWidth: 2,
          crosshairMarkerRadius: 6,
          priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
        });
        areaSeries.setData(uniqueLineData);
        candlestickSeriesRef.current = areaSeries;

        // ── Wave Markers ──────────────────────────────────────
        if (showWaveLabels) {
          const markers = waves
            .filter((w) => w.price != null && (w.timestamp || w.index != null))
            .map((w, idx) => {
              const waveNum = w.wave_number || `${idx + 1}`;
              const isImpulse = patternType === 'impulse';
              const colors = isImpulse ? WAVE_COLORS.impulse : WAVE_COLORS.correction;
              const color = colors[waveNum] || '#60a5fa';
              const isPeak = w.wave_type === 'peak';

              return {
                time: w.timestamp
                  ? Math.floor(new Date(w.timestamp).getTime() / 1000)
                  : Math.floor(Date.now() / 1000) - (waves.length - idx) * (timeframe === '1day' ? 86400 : timeframe === '1hour' ? 3600 : timeframe === '15min' ? 900 : 300),
                position: isPeak ? 'aboveBar' : 'belowBar',
                color: color,
                shape: isPeak ? 'arrowDown' : 'arrowUp',
                text: `W${waveNum}`,
              };
            })
            .sort((a, b) => a.time - b.time);

          // Deduplicate by time
          const uniqueMarkers = [];
          const markerTimes = new Set();
          for (const m of markers) {
            if (!markerTimes.has(m.time)) {
              markerTimes.add(m.time);
              uniqueMarkers.push(m);
            }
          }

          if (uniqueMarkers.length > 0) {
            areaSeries.setMarkers(uniqueMarkers);
            markersRef.current = uniqueMarkers;
          }
        }

        // ── Fibonacci Price Lines ─────────────────────────────
        if (showFibLevels && analysis.fibonacci_targets) {
          const fibTargets = analysis.fibonacci_targets;
          const allFibLines = [];

          Object.entries(fibTargets).forEach(([targetGroup, levels]) => {
            if (levels && typeof levels === 'object') {
              Object.entries(levels).forEach(([level, price]) => {
                if (price != null && typeof price === 'number') {
                  const fibColor = FIBONACCI_COLORS[level] || '#94a3b8';
                  allFibLines.push(
                    areaSeries.createPriceLine({
                      price: price,
                      color: fibColor,
                      lineWidth: 1,
                      lineStyle: LineStyle.Dashed,
                      axisLabelVisible: true,
                      title: `${targetGroup.replace('_targets', '').replace('wave_', 'W')} ${level}`,
                    })
                  );
                }
              });
            }
          });
          fibLinesRef.current = allFibLines;
        }

        // ── Channel Lines ─────────────────────────────────────
        if (showChannels && analysis.channel_targets) {
          const ch = analysis.channel_targets;
          const channelLines = [];

          if (ch.wave_3_channel_target != null) {
            channelLines.push(
              areaSeries.createPriceLine({
                price: ch.wave_3_channel_target,
                color: CHANNEL_COLORS.upper,
                lineWidth: 2,
                lineStyle: LineStyle.Dotted,
                axisLabelVisible: true,
                title: 'W3 Channel',
              })
            );
          }
          if (ch.wave_5_channel_target != null) {
            channelLines.push(
              areaSeries.createPriceLine({
                price: ch.wave_5_channel_target,
                color: CHANNEL_COLORS.upper,
                lineWidth: 2,
                lineStyle: LineStyle.Dotted,
                axisLabelVisible: true,
                title: 'W5 Channel',
              })
            );
          }
          if (ch.wave_4_channel_target != null) {
            channelLines.push(
              areaSeries.createPriceLine({
                price: ch.wave_4_channel_target,
                color: CHANNEL_COLORS.lower,
                lineWidth: 2,
                lineStyle: LineStyle.Dotted,
                axisLabelVisible: true,
                title: 'W4 Channel',
              })
            );
          }
          channelLinesRef.current = channelLines;
        }

        // ── Prediction Target / Stop Loss lines ───────────────
        if (analysis.prediction) {
          const pred = analysis.prediction;
          if (pred.target_price != null) {
            areaSeries.createPriceLine({
              price: pred.target_price,
              color: '#22c55e',
              lineWidth: 2,
              lineStyle: LineStyle.LargeDashed,
              axisLabelVisible: true,
              title: 'Target',
            });
          }
          if (pred.stop_loss != null) {
            areaSeries.createPriceLine({
              price: pred.stop_loss,
              color: '#ef4444',
              lineWidth: 2,
              lineStyle: LineStyle.LargeDashed,
              axisLabelVisible: true,
              title: 'Stop Loss',
            });
          }
        }

        chart.timeScale().fitContent();
      }
    }

    // Resize handler
    const handleResize = () => {
      if (chartInstanceRef.current && container) {
        chartInstanceRef.current.applyOptions({ width: container.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartInstanceRef.current) {
        chartInstanceRef.current.remove();
        chartInstanceRef.current = null;
      }
    };
  }, [analysis, showFibLevels, showChannels, showWaveLabels, timeframe]);

  // ═══════════════════════════════════════════════════════════════
  // ─── RENDER ───────────────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════

  const waveStructure = analysis?.wave_structure || {};
  const pattern = analysis?.pattern || {};
  const prediction = analysis?.prediction || {};
  const fibTargets = analysis?.fibonacci_targets || {};
  const channelTargets = analysis?.channel_targets || {};
  const mtfData = analysis?.multi_timeframe_confirmation;

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <Navbar />

      <main className="flex-grow max-w-[1600px] w-full mx-auto px-4 py-6">
        {/* ── Page Header ───────────────────────────────────── */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
            <span className="text-blue-400">~</span>
            Elliott Wave Analyzer
          </h1>
          <p className="text-gray-400 text-sm">
            Automated Elliott Wave pattern identification with Fibonacci targets, channel projections, and multi-timeframe confirmation
          </p>
        </div>

        {/* ── Controls Bar ──────────────────────────────────── */}
        <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            {/* Symbol Input */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Symbol</label>
              <input
                type="text"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleAnalyze()}
                placeholder="NIFTY50, RELIANCE, TATASTEEL..."
                className="w-full bg-slate-700/80 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
              />
            </div>

            {/* Timeframe Selector */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Timeframe</label>
              <div className="flex rounded-lg overflow-hidden border border-slate-600">
                {TIMEFRAMES.map((tf) => (
                  <button
                    key={tf.value}
                    onClick={() => setTimeframe(tf.value)}
                    className={`px-4 py-2.5 text-sm font-medium transition-all ${
                      timeframe === tf.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-700/80 text-gray-400 hover:bg-slate-600 hover:text-gray-200'
                    }`}
                  >
                    {tf.label}
                  </button>
                ))}
              </div>
            </div>

            {/* MTF Toggle */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Multi-TF</label>
              <button
                onClick={() => setIncludeMtf(!includeMtf)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                  includeMtf
                    ? 'bg-emerald-600/20 text-emerald-400 border-emerald-500/40'
                    : 'bg-slate-700/80 text-gray-400 border-slate-600 hover:bg-slate-600'
                }`}
              >
                {includeMtf ? 'MTF ON' : 'MTF OFF'}
              </button>
            </div>

            {/* Analyze Button */}
            <button
              onClick={handleAnalyze}
              disabled={loading}
              className={`px-8 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                loading
                  ? 'bg-blue-600/50 text-blue-300 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-500 shadow-lg shadow-blue-600/20 hover:shadow-blue-500/30'
              }`}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Analyzing...
                </span>
              ) : (
                'Analyze'
              )}
            </button>
          </div>
        </div>

        {/* ── Error Display ─────────────────────────────────── */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
            <span className="text-red-400 text-lg mt-0.5">!</span>
            <div>
              <p className="text-red-300 font-medium">Analysis Error</p>
              <p className="text-red-400/80 text-sm mt-1">{error}</p>
            </div>
            <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300">
              &times;
            </button>
          </div>
        )}

        {/* ── Tab Switcher ──────────────────────────────────── */}
        <div className="flex gap-1 mb-6">
          {[
            { id: 'chart', label: 'Chart & Analysis' },
            { id: 'history', label: 'Historical Analyses' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-800/60 text-gray-400 hover:bg-slate-700/60 hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ═══════════════════════════════════════════════════ */}
        {/* ─── CHART & ANALYSIS TAB ─────────────────────── */}
        {/* ═══════════════════════════════════════════════════ */}
        {activeTab === 'chart' && (
          <>
            {/* ── Quick Summary Bar ─────────────────────────── */}
            {analysis && (
              <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 mb-6">
                <div className="flex flex-wrap items-center gap-6">
                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wider">Symbol</span>
                    <p className="text-lg font-bold text-white">{analysis.symbol}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wider">Timeframe</span>
                    <p className="text-lg font-semibold text-gray-200">{analysis.timeframe}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wider">Trend</span>
                    <div className="mt-0.5">
                      <DirectionBadge direction={waveStructure.trend_direction} />
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wider">Pattern</span>
                    <p className="text-lg font-semibold text-blue-300 capitalize">
                      {waveStructure.pattern_type || '-'} {pattern.pattern_name ? `(${pattern.pattern_name})` : ''}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wider">Current Wave</span>
                    <p className="text-lg font-bold text-amber-400">{waveStructure.current_position || '-'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wider">Pivots</span>
                    <p className="text-lg font-semibold text-gray-300">{analysis.pivot_count ?? '-'}</p>
                  </div>
                  <div className="ml-auto">
                    <ConfidenceGauge value={analysis.confidence_score} label="Overall" size="md" />
                  </div>
                </div>
              </div>
            )}

            {/* ── Chart + Sidebar Layout ────────────────────── */}
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 mb-6">
              {/* Chart Area (3/4 width) */}
              <div className="xl:col-span-3">
                <InfoCard
                  title="Wave Structure Chart"
                  headerRight={
                    analysis && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowWaveLabels(!showWaveLabels)}
                          className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                            showWaveLabels ? 'bg-blue-600/30 text-blue-300 border border-blue-500/40' : 'bg-slate-700 text-gray-500 border border-slate-600'
                          }`}
                        >
                          Waves
                        </button>
                        <button
                          onClick={() => setShowFibLevels(!showFibLevels)}
                          className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                            showFibLevels ? 'bg-amber-600/30 text-amber-300 border border-amber-500/40' : 'bg-slate-700 text-gray-500 border border-slate-600'
                          }`}
                        >
                          Fib
                        </button>
                        <button
                          onClick={() => setShowChannels(!showChannels)}
                          className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                            showChannels ? 'bg-green-600/30 text-green-300 border border-green-500/40' : 'bg-slate-700 text-gray-500 border border-slate-600'
                          }`}
                        >
                          Channels
                        </button>
                      </div>
                    )
                  }
                >
                  <div ref={chartContainerRef} className="w-full" style={{ minHeight: '520px' }}>
                    {!analysis && !loading && (
                      <div className="flex flex-col items-center justify-center h-[520px] text-gray-500">
                        <svg className="w-16 h-16 mb-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                        </svg>
                        <p className="text-lg font-medium">Enter a symbol and click Analyze</p>
                        <p className="text-sm text-gray-600 mt-1">Wave patterns will be visualized here</p>
                      </div>
                    )}
                    {loading && (
                      <div className="flex flex-col items-center justify-center h-[520px]">
                        <div className="relative">
                          <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-blue-500 animate-spin" />
                        </div>
                        <p className="text-gray-400 mt-4">Running Elliott Wave analysis...</p>
                        <p className="text-gray-600 text-sm mt-1">Detecting pivots, labeling waves, classifying patterns</p>
                      </div>
                    )}
                  </div>
                </InfoCard>
              </div>

              {/* Sidebar (1/4 width) */}
              <div className="xl:col-span-1 space-y-4">
                {/* Wave Points Table */}
                <InfoCard title="Wave Points">
                  {waveStructure.waves?.length > 0 ? (
                    <div className="space-y-2">
                      {waveStructure.waves.map((w, idx) => {
                        const isImpulse = waveStructure.pattern_type === 'impulse';
                        const colors = isImpulse ? WAVE_COLORS.impulse : WAVE_COLORS.correction;
                        const color = colors[w.wave_number] || '#94a3b8';
                        return (
                          <div
                            key={idx}
                            className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-700/40"
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                                style={{ backgroundColor: color }}
                              >
                                {w.wave_number}
                              </span>
                              <span className="text-xs text-gray-400 capitalize">{w.wave_type}</span>
                            </div>
                            <span className="text-sm font-mono text-gray-200">
                              {typeof w.price === 'number' ? w.price.toFixed(2) : '-'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-4">No wave data yet</p>
                  )}
                </InfoCard>

                {/* Pattern Details */}
                <InfoCard title="Pattern Classification">
                  {pattern.pattern_name ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Pattern</span>
                        <span className="text-white font-semibold capitalize">{pattern.pattern_name}</span>
                      </div>
                      <ConfidenceGauge value={pattern.confidence} label="Pattern Confidence" size="sm" />
                      {pattern.pattern_details && Object.keys(pattern.pattern_details).length > 0 && (
                        <div className="mt-2 space-y-1.5 border-t border-slate-700/50 pt-3">
                          {Object.entries(pattern.pattern_details).map(([key, val]) => (
                            <div key={key} className="flex justify-between text-xs">
                              <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}</span>
                              <span className="text-gray-300 font-mono">
                                {typeof val === 'number' ? val.toFixed(4) : String(val)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-4">No pattern classified yet</p>
                  )}
                </InfoCard>
              </div>
            </div>

            {/* ── Bottom Analysis Panels ─────────────────────── */}
            {analysis && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-6">
                {/* ── Prediction Dashboard ───────────────────── */}
                <InfoCard title="Prediction Dashboard" className="md:col-span-2 xl:col-span-1">
                  {prediction.direction ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <DirectionBadge direction={prediction.direction} />
                        <ConfidenceGauge value={prediction.confidence} label="Confidence" size="sm" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                          <span className="text-xs text-green-400/70 uppercase">Target</span>
                          <p className="text-lg font-bold text-green-400 font-mono">
                            {prediction.target_price?.toFixed(2) ?? '-'}
                          </p>
                        </div>
                        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                          <span className="text-xs text-red-400/70 uppercase">Stop Loss</span>
                          <p className="text-lg font-bold text-red-400 font-mono">
                            {prediction.stop_loss?.toFixed(2) ?? '-'}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2 border-t border-slate-700/50 pt-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Probability</span>
                          <span className="text-white font-semibold">{((prediction.probability || 0) * 100).toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all duration-700"
                            style={{
                              width: `${(prediction.probability || 0) * 100}%`,
                              backgroundColor: prediction.probability >= 0.6 ? '#22c55e' : prediction.probability >= 0.4 ? '#f59e0b' : '#ef4444',
                            }}
                          />
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Time Horizon</span>
                          <span className="text-gray-200 capitalize">{prediction.time_horizon || '-'}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Next Move</span>
                          <span className="text-gray-200 text-right max-w-[60%]">{prediction.next_expected_move || '-'}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-8">No prediction available</p>
                  )}
                </InfoCard>

                {/* ── Fibonacci Targets ───────────────────────── */}
                <InfoCard title="Fibonacci Targets">
                  {Object.keys(fibTargets).length > 0 ? (
                    <div className="space-y-4 max-h-[340px] overflow-y-auto pr-1 custom-scrollbar">
                      {Object.entries(fibTargets).map(([group, levels]) => {
                        if (!levels || typeof levels !== 'object' || Object.keys(levels).length === 0) return null;
                        return (
                          <div key={group}>
                            <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">
                              {group.replace(/_targets/g, '').replace(/wave_/g, 'Wave ').replace(/_/g, ' ')}
                            </h4>
                            <div className="space-y-1">
                              {Object.entries(levels).map(([level, price]) => (
                                <div key={level} className="flex items-center justify-between py-1 px-2 rounded bg-slate-700/30 hover:bg-slate-700/50 transition-colors">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className="w-2.5 h-2.5 rounded-full"
                                      style={{ backgroundColor: FIBONACCI_COLORS[level] || '#94a3b8' }}
                                    />
                                    <span className="text-sm text-gray-400">{level}</span>
                                  </div>
                                  <span className="text-sm font-mono text-gray-200">
                                    {typeof price === 'number' ? price.toFixed(2) : '-'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-8">No Fibonacci targets computed</p>
                  )}
                </InfoCard>

                {/* ── Channel Projections ─────────────────────── */}
                <InfoCard title="Channel Projections">
                  {(channelTargets.wave_3_channel_target || channelTargets.wave_4_channel_target || channelTargets.wave_5_channel_target) ? (
                    <div className="space-y-3">
                      {[
                        { key: 'wave_3_channel_target', label: 'Wave 3 Target', color: CHANNEL_COLORS.upper },
                        { key: 'wave_4_channel_target', label: 'Wave 4 Target', color: CHANNEL_COLORS.lower },
                        { key: 'wave_5_channel_target', label: 'Wave 5 Target', color: CHANNEL_COLORS.upper },
                      ].map(
                        ({ key, label, color }) =>
                          channelTargets[key] != null && (
                            <div key={key} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-700/40">
                              <div className="flex items-center gap-2">
                                <span className="w-3 h-0.5 rounded" style={{ backgroundColor: color }} />
                                <span className="text-sm text-gray-400">{label}</span>
                              </div>
                              <span className="text-lg font-mono font-semibold text-gray-200">
                                {channelTargets[key]?.toFixed(2)}
                              </span>
                            </div>
                          )
                      )}
                      {channelTargets.channel_lines && (
                        <div className="mt-3 border-t border-slate-700/50 pt-3">
                          <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Channel Lines</h4>
                          {Object.entries(channelTargets.channel_lines).map(([lineType, coords]) => (
                            <div key={lineType} className="flex items-center justify-between text-xs py-1">
                              <span className="text-gray-400 capitalize">{lineType}</span>
                              <span className="text-gray-500 font-mono">
                                {Array.isArray(coords) ? coords.map((c) => (typeof c === 'number' ? c.toFixed(2) : c)).join(' , ') : '-'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-8">No channel projections available</p>
                  )}
                </InfoCard>
              </div>
            )}

            {/* ── Multi-Timeframe Confirmation ──────────────── */}
            {analysis && mtfData && (
              <InfoCard title="Multi-Timeframe Confirmation" className="mb-6">
                <div className="space-y-4">
                  {/* MTF Summary */}
                  <div className="flex flex-wrap items-center gap-6">
                    <div className="flex items-center gap-3">
                      <span
                        className={`w-4 h-4 rounded-full ${
                          mtfData.confirmed ? 'bg-green-500 shadow-lg shadow-green-500/30' : 'bg-red-500 shadow-lg shadow-red-500/30'
                        }`}
                      />
                      <span className={`text-lg font-semibold ${mtfData.confirmed ? 'text-green-400' : 'text-red-400'}`}>
                        {mtfData.confirmed ? 'Confirmed' : 'Not Confirmed'}
                      </span>
                    </div>
                    {mtfData.direction && <DirectionBadge direction={mtfData.direction} />}
                    <ConfidenceGauge value={mtfData.confidence} label="MTF Confidence" size="sm" />
                  </div>

                  {/* Timeframe Breakdown */}
                  {mtfData.timeframe_analyses && Object.keys(mtfData.timeframe_analyses).length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                      {Object.entries(mtfData.timeframe_analyses).map(([tf, tfAnalysis]) => (
                        <div
                          key={tf}
                          onClick={() => setMtfTimeframe(mtfTimeframe === tf ? null : tf)}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            mtfTimeframe === tf
                              ? 'bg-blue-600/20 border-blue-500/40'
                              : 'bg-slate-700/40 border-slate-600/30 hover:bg-slate-700/60'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-gray-200">{tf}</span>
                            {tfAnalysis?.wave_structure?.trend_direction && (
                              <DirectionBadge direction={tfAnalysis.wave_structure.trend_direction} className="text-xs px-2 py-0.5" />
                            )}
                          </div>
                          <div className="text-xs text-gray-400 space-y-1">
                            <div className="flex justify-between">
                              <span>Pattern</span>
                              <span className="text-gray-300 capitalize">{tfAnalysis?.wave_structure?.pattern_type || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Wave</span>
                              <span className="text-gray-300">{tfAnalysis?.wave_structure?.current_position || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Confidence</span>
                              <span className="text-gray-300">{tfAnalysis?.wave_structure?.confidence != null ? `${(tfAnalysis.wave_structure.confidence * 100).toFixed(0)}%` : '-'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Expanded MTF detail */}
                  {mtfTimeframe && mtfData.timeframe_analyses?.[mtfTimeframe] && (
                    <div className="mt-4 p-4 bg-slate-700/30 rounded-lg border border-slate-600/30">
                      <h4 className="text-sm font-semibold text-gray-200 mb-3">
                        {mtfTimeframe} - Detailed View
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        {mtfData.timeframe_analyses[mtfTimeframe]?.wave_structure?.waves?.map((w, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-xs font-bold text-white">
                              {w.wave_number}
                            </span>
                            <span className="text-gray-400">{w.wave_type}</span>
                            <span className="text-gray-200 font-mono ml-auto">{w.price?.toFixed(2) ?? '-'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </InfoCard>
            )}
          </>
        )}

        {/* ═══════════════════════════════════════════════════ */}
        {/* ─── HISTORY TAB ──────────────────────────────── */}
        {/* ═══════════════════════════════════════════════════ */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            {/* Symbol History */}
            <InfoCard
              title={`Analysis History - ${symbol}`}
              headerRight={
                <button
                  onClick={loadHistory}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Refresh
                </button>
              }
            >
              {history.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 text-xs uppercase tracking-wider border-b border-slate-700/50">
                        <th className="text-left pb-3 pr-4">Date</th>
                        <th className="text-left pb-3 pr-4">Timeframe</th>
                        <th className="text-left pb-3 pr-4">Pattern</th>
                        <th className="text-left pb-3 pr-4">Direction</th>
                        <th className="text-right pb-3 pr-4">Predicted Target</th>
                        <th className="text-right pb-3 pr-4">Actual Target</th>
                        <th className="text-center pb-3 pr-4">Hit</th>
                        <th className="text-right pb-3">Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((record) => (
                        <tr key={record.id} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors">
                          <td className="py-3 pr-4 text-gray-300 font-mono text-xs">
                            {record.start_date ? new Date(record.start_date).toLocaleDateString() : '-'}
                          </td>
                          <td className="py-3 pr-4 text-gray-400">{record.timeframe || '-'}</td>
                          <td className="py-3 pr-4">
                            <span className="text-gray-200 capitalize">{record.pattern_type || '-'}</span>
                            {record.pattern_name && (
                              <span className="text-gray-500 text-xs ml-1">({record.pattern_name})</span>
                            )}
                          </td>
                          <td className="py-3 pr-4">
                            {record.predicted_direction && <DirectionBadge direction={record.predicted_direction} className="text-xs px-2 py-0.5" />}
                          </td>
                          <td className="py-3 pr-4 text-right font-mono text-gray-300">
                            {record.predicted_target?.toFixed(2) ?? '-'}
                          </td>
                          <td className="py-3 pr-4 text-right font-mono text-gray-300">
                            {record.actual_target?.toFixed(2) ?? '-'}
                          </td>
                          <td className="py-3 pr-4 text-center">
                            {record.target_hit === true && <span className="text-green-400">Yes</span>}
                            {record.target_hit === false && <span className="text-red-400">No</span>}
                            {record.target_hit == null && <span className="text-gray-600">-</span>}
                          </td>
                          <td className="py-3 text-right font-mono">
                            {record.confidence_at_prediction != null ? (
                              <span className={`${record.confidence_at_prediction >= 0.6 ? 'text-green-400' : record.confidence_at_prediction >= 0.4 ? 'text-yellow-400' : 'text-red-400'}`}>
                                {(record.confidence_at_prediction * 100).toFixed(0)}%
                              </span>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg">No analysis history for {symbol}</p>
                  <p className="text-sm mt-1">Run an analysis first to build history</p>
                </div>
              )}
            </InfoCard>

            {/* Recent Analyses (all symbols) */}
            <InfoCard title="Recent Analyses (All Symbols)">
              {recentAnalyses.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {recentAnalyses.map((record, idx) => (
                    <div
                      key={idx}
                      className="bg-slate-700/40 border border-slate-600/30 rounded-lg p-3 hover:bg-slate-700/60 transition-colors cursor-pointer"
                      onClick={() => {
                        setSymbol(record.symbol);
                        setTimeframe(record.timeframe || '5min');
                        setActiveTab('chart');
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-semibold">{record.symbol}</span>
                        <span className="text-xs text-gray-500">{record.timeframe}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400 capitalize">{record.pattern_type || '-'}</span>
                        {record.confidence_score != null && (
                          <span className={`font-mono ${record.confidence_score >= 0.6 ? 'text-green-400' : 'text-yellow-400'}`}>
                            {(record.confidence_score * 100).toFixed(0)}%
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {record.analysis_date ? new Date(record.analysis_date).toLocaleString() : ''}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p>No recent analyses found</p>
                </div>
              )}
            </InfoCard>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default ElliottWavePage;
